import { find, get, includes, isEmpty, some } from "lodash";
import type { RootState } from "types/state";
import moment from "moment-timezone";
import {
  allPayrollIntegrations,
  autoApproveJoinRequestsId,
  daysBeforeDebitId,
  debitAuthorizationIntegrations,
  disableReferralsId,
  payrollIntegrations180,
  payrollIntegrations360,
  statePlanId,
  ukgPayrollIntegrationId,
  uniqueInviteCodesId,
  virtualBankId,
} from "statics/featureIds";
import { auth0PlanCodeSelector } from "./auth0";
import { ENTERPRISE } from "statics/planCodes";
import { ConnectionActive } from "statics/payrollIntegrationStates";
import {
  EmployerApplicationApproved,
  EmployerApplicationSubmittedKYB,
  EmployerBillingDetailsComplete,
  EmployerBillingDetailsPending,
  EmployerBillingVerificationPending,
  EmployerRegistrationComplete,
  EmployerRequiredUpdatesPending,
  EmployerSelfServeBillingDetailsComplete,
  EmployerSelfServeBillingDetailsPending,
  progressiveOnboardingStates as progessiveOnboardingStates,
} from "statics/states";
import { ISOLVED_WHOLESALE } from "statics/billingTypes";

export const hasInvitedAUserSelector = (state: RootState) => {
  return (
    !isEmpty(get(state, "employer.employeeInvitations")) ||
    !isEmpty(get(state, "employer.employerLinkRequests"))
  );
};

export const hasStatePlanSelector = (state: RootState) => {
  const planType = get(state, "employer.company.enabledProductFeatures") as
    | unknown[]
    | undefined;
  return some(planType, (key) => key === statePlanId);
};

export const hasExpiredInvites = (state: RootState) => {
  const enabledFeatures = get(
    state,
    "employer.company.enabledProductFeatures",
    []
  ) as unknown[];
  return some(
    enabledFeatures,
    (featureId) => featureId === uniqueInviteCodesId
  );
};

export const hasEnabledDaysBeforeDebit = (state: RootState) => {
  const enabledFeatures = get(
    state,
    "employer.company.enabledProductFeatures",
    []
  ) as unknown[];
  return some(enabledFeatures, (featureId) => featureId === daysBeforeDebitId);
};

export const hasVirtualBankEnabled = (state: RootState) => {
  const enabledFeatures = get(
    state,
    "employer.company.enabledProductFeatures",
    []
  ) as unknown[];
  return some(
    enabledFeatures,
    (hasVirtualBank) => hasVirtualBank === virtualBankId
  );
};
// Quick fix for making selectors work with the new state machine for progressive onboarding
const progressiveOnboardingStateScoring: Record<string, number> = {
  [progessiveOnboardingStates.ProgressiveOnboardingStarted]: 0,
  [progessiveOnboardingStates.AddedBankAccount]: 1,
  [progessiveOnboardingStates.AddedGroups]: 2,
  [progessiveOnboardingStates.AddedDeductionCode]: 3,
  [progessiveOnboardingStates.AnnouncedIcon]: 4,
  [progessiveOnboardingStates.InvitedEmployees]: 5,
  [progessiveOnboardingStates.DeductionCodeAfterInviteEmployees]: 6,
  [progessiveOnboardingStates.LaunchedPlan]: 7,
  [progessiveOnboardingStates.DeductionCodeAfterLaunchPlan]: 8,
};

export const hasAddedBank = (state: RootState) => {
  const progState = get(
    state,
    "employer.company.progressiveOnboardingState",
    ""
  ) as string;
  const progScore = progressiveOnboardingStateScoring[progState];
  return progScore >= progressiveOnboardingStateScoring.AddedBankAccount;
};

export const hasAnnouncedIcon = (state: RootState) => {
  const progState = get(
    state,
    "employer.company.progressiveOnboardingState",
    ""
  ) as string;
  const progScore = progressiveOnboardingStateScoring[progState];
  return progScore >= progressiveOnboardingStateScoring.AnnouncedIcon;
};

export const hasInvitedEmployees = (state: RootState) => {
  const progState = get(
    state,
    "employer.company.progressiveOnboardingState",
    ""
  ) as string;
  const progScore = progressiveOnboardingStateScoring[progState];
  return progScore >= progressiveOnboardingStateScoring.InvitedEmployees;
};

export const hasLaunchedPlan = (state: RootState) => {
  const progState = get(
    state,
    "employer.company.progressiveOnboardingState",
    ""
  ) as string;
  const progScore = progressiveOnboardingStateScoring[progState];
  return progScore >= progressiveOnboardingStateScoring.LaunchedPlan;
};

export const hasAddedGroups = (state: RootState) => {
  const progState = get(
    state,
    "employer.company.progressiveOnboardingState",
    ""
  ) as string;
  const progScore = progressiveOnboardingStateScoring[progState];
  return progScore >= progressiveOnboardingStateScoring.AddedGroups;
};

export const hasEnabled360PayrollIntegration = (state: RootState) => {
  const enabledFeatures = get(
    state,
    "employer.company.enabledProductFeatures",
    []
  ) as unknown[];
  return some(enabledFeatures, (featureId) => {
    return includes(payrollIntegrations360, featureId);
  });
};

export const hasAddedDeductionCode = (state: RootState) => {
  const has360PayrollIntegration = hasEnabled360PayrollIntegration(state);

  if (has360PayrollIntegration) {
    return true;
  }

  const progState = get(
    state,
    "employer.company.progressiveOnboardingState",
    ""
  ) as string;
  const progScore = progressiveOnboardingStateScoring[progState];

  const needsDeductionCodeState =
    progState === "DeductionCodeAfterInviteEmployees" ||
    progState === "DeductionCodeAfterLaunchPlan";

  return (
    progScore >= progressiveOnboardingStateScoring.AddedDeductionCode &&
    !needsDeductionCodeState
  );
};

export const hasFullyOnboarded = (state: RootState) => {
  const launchedPlan = hasLaunchedPlan(state);

  return launchedPlan;
};

export const planCodeSelector = (state: RootState) => {
  const storedPlanCode = get(state.employer, "company.planCode");
  const initialPlanCode = auth0PlanCodeSelector(state);
  const planCode = storedPlanCode ? storedPlanCode : ENTERPRISE;

  // If there was no initial plan code specified, then give back nothing here
  if (initialPlanCode === undefined || !initialPlanCode) {
    return null;
  }

  return planCode;
};

export const hasEnabled180PayrollIntegration = (state: RootState) => {
  const enabledFeatures = get(
    state,
    "employer.company.enabledProductFeatures",
    []
  ) as unknown[];
  return some(enabledFeatures, (featureId) => {
    return includes(payrollIntegrations180, featureId);
  });
};

export const hasEnabledPayrollIntegration = (state: RootState) => {
  const enabledFeatures = get(
    state,
    "employer.company.enabledProductFeatures",
    []
  ) as unknown[];

  return some(enabledFeatures, (featureId) =>
    includes(allPayrollIntegrations, featureId)
  );
};

export const hasEnabledUkgPayrollIntegration = (state: RootState) => {
  const enabledFeatures = get(
    state,
    "employer.company.enabledProductFeatures",
    []
  ) as unknown[];
  return includes(enabledFeatures, ukgPayrollIntegrationId);
};

export const hasRunFirstPayroll = (state: RootState) => {
  const hasRanPayroll = get(
    state,
    "employer.company.hasRunFirstPayroll",
    false
  );
  return hasRanPayroll;
};

export const canReferCompany = (state: RootState) => {
  const enabledFeatures = get(
    state,
    "employer.company.enabledProductFeatures",
    []
  ) as unknown[];

  const disabledReferrals = includes(enabledFeatures, disableReferralsId);

  return hasRunFirstPayroll(state) && !disabledReferrals;
};

export const employerContributionAbility = (state: RootState) => {
  const userContributionAbilities = get(
    state,
    "employer.contributionAbility",
    {}
  );
  return userContributionAbilities;
};

export const userContributionAbilities = (state: RootState) => {
  return get(
    employerContributionAbility(state),
    "userContributionAbilities",
    []
  );
};

export const requiresBillingMicroDepositVerification = (state: RootState) => {
  const setupIntent = get(state, "employer.setupIntent") as {
    status?: string;
    last_setup_error?: unknown;
  };
  const requiresMicroDepositVerification =
    !isEmpty(setupIntent) && setupIntent.status === "requires_action";

  return requiresMicroDepositVerification;
};

export const requiresBillingPaymentUpdate = (state: RootState) => {
  const setupIntent = get(state, "employer.setupIntent") as {
    status?: string;
    last_setup_error?: unknown;
  };
  if (isEmpty(setupIntent)) return false;

  const requiresPaymentMethod =
    setupIntent.status === "requires_payment_method" &&
    !isEmpty(setupIntent.last_setup_error);

  return requiresPaymentMethod;
};

// saved, waiting on the webhook or the payment requirement to release a held dashboard
export const setupIntentSucceeded = (state: RootState) =>
  get(state, "employer.setupIntent.status") === "succeeded";

export const setupIntentHasError = (state: RootState) => {
  const setupIntent = get(state, "employer.setupIntent") as {
    status?: string;
    last_setup_error?: unknown;
  };
  if (isEmpty(setupIntent)) return false;

  return !isEmpty(setupIntent.last_setup_error);
};

// order companies are asked for a payment method in case their invoice goes unpaid
export const collectsPaymentMethodSelector = (state: RootState) =>
  !!state.employer.paymentRequirement.collectsPaymentMethod;

// the backend only sends an order company through billing while it needs a payment method, once there the step
// stays in the progress bar even after a card is saved
export const showsOrderBillingStepSelector = (state: RootState) =>
  !!state.employer.paymentRequirement.requiresPaymentMethod ||
  includes(
    [
      EmployerBillingDetailsPending,
      EmployerBillingDetailsComplete,
      EmployerSelfServeBillingDetailsPending,
      EmployerSelfServeBillingDetailsComplete,
    ],
    get(state.user, "companyState.state")
  );

export const openInvoiceAmountDueSelector = (state: RootState) =>
  state.employer.paymentRequirement.openInvoiceAmountDue ?? 0;

// set while a deferred order's billing hasn't started
export const billingStartDateSelector = (state: RootState) =>
  state.employer.paymentRequirement.billingStartDate ?? null;

// only the onboarding state holds the dashboard for billing, the setup intent just picks the message
export const billingVerificationPending = (state: RootState) =>
  get(state.user, "companyState.state") === EmployerBillingVerificationPending;

export const requiresBillingDetails = (state: RootState) => {
  const billingStates = [
    EmployerBillingDetailsPending,
    EmployerSelfServeBillingDetailsPending,
    EmployerApplicationSubmittedKYB,
    EmployerBillingVerificationPending,
    EmployerRegistrationComplete,
  ];

  const companyState = get(state.user, "companyState.state");
  return includes(billingStates, companyState);
};

export const requiresAdministrativeUpdates = (state: RootState) => {
  const companyState = get(state.user, "companyState.state");
  return companyState === EmployerRequiredUpdatesPending;
};

export const pendingEmployerRequiredUpdates = (state: RootState) => {
  return get(state, "employer.requiredUpdates.pendingSteps", []);
};

export const isIsolvedWholesale = (state: RootState) => {
  const billingType = get(state, "employer.company.billingType", "");
  return billingType === ISOLVED_WHOLESALE;
};

export const employerHasFullyKybd = (state: RootState) => {
  const companyState = get(state.user, "companyState.state");
  return (
    companyState === EmployerRequiredUpdatesPending ||
    companyState === EmployerApplicationApproved
  );
};

export const getLimitByType = (state: RootState, limitKey: string) => {
  const limit = find(
    get(state, "employer.company.limits") as Array<{
      key?: string;
      value?: unknown;
    }>,
    { key: limitKey }
  );
  return get(limit, "value");
};

export const hasAutoApprovalLinkRequestsFeature = (state: RootState) => {
  const enabledFeatures = get(
    state,
    "employer.company.enabledProductFeatures",
    []
  ) as unknown[];
  return includes(enabledFeatures, autoApproveJoinRequestsId);
};

export const getPayrollIntegrationStatus = (state: RootState) => {
  return get(state, "employer.payrollIntegrationStatus");
};

export const hasAcknowledgedRecurringDebits = (state: RootState) => {
  const companyId = get(state, "employer.company.id");
  return (
    !!companyId &&
    state.employer.recurringDebitsAcknowledgedCompanyId === companyId
  );
};

export const usesDebitAuthorization = (state: RootState) => {
  const enabledFeatures = get(
    state,
    "employer.company.enabledProductFeatures",
    []
  ) as unknown[];
  return some(enabledFeatures, (featureId) => {
    return includes(debitAuthorizationIntegrations, featureId);
  });
};

export const hasSuccessfullyConnectedPayroll = (state: RootState) => {
  const payrollIntegrationStatus = get(
    state,
    "employer.payrollIntegrationStatus"
  );
  return (
    payrollIntegrationStatus && payrollIntegrationStatus === ConnectionActive
  );
};

export const getEmployerPassportAgreementDate = (state: RootState) => {
  const companyAgreements = get(
    state,
    "employer.companyAgreements",
    []
  ) as Array<{
    type?: string;
    agreementDate?: string;
  }>;
  const passportAgreement = find(companyAgreements, {
    type: "passport_agreement",
  });
  if (!passportAgreement) {
    return "";
  }
  return passportAgreement.agreementDate;
};

export const getEmployerWalletDetails = (state: RootState) => {
  return get(state, "employer.employerWalletDetails");
};

export const getEmployerCRBAgreementDate = (state: RootState) => {
  const companyAgreements = get(
    state,
    "employer.companyAgreements",
    []
  ) as Array<{
    type?: string;
    agreementDate?: string;
  }>;
  const crbAgreement = find(companyAgreements, {
    type: "crb_agreement",
  });
  if (!crbAgreement) {
    return "";
  }
  const crbAgreementDate = moment
    .utc(crbAgreement.agreementDate)
    .tz("America/New_York")
    .format("MMMM D, YYYY");
  return crbAgreementDate;
};
